import React, { useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import DatasetViewer, { DatasetField } from "../datasetViewer/DatasetViewer";
import { SourceDataRecord } from "../types";
import {
  Search as SearchIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";

interface CRSearchDatasetViewerProps {
  toolResult: any;
}

const CRSearchDatasetViewer: React.FC<CRSearchDatasetViewerProps> = ({
  toolResult,
}) => {
  const [maxResults, setMaxResults] = useState<number>(25);
  const [currentData, setCurrentData] = useState<SourceDataRecord[]>([]);
  const [currentFields, setCurrentFields] = useState<DatasetField[]>([]);

  if (!toolResult || !toolResult.dataset_id) {
    return (
      <Paper sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          No dataset specified in search result
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          The Smart Search tool result should include a dataset_id
        </Typography>
      </Paper>
    );
  }

  const handleMaxResultsChange = (value: number) => {
    const clampedValue = Math.max(1, Math.min(1000, value));
    setMaxResults(clampedValue);
  };

  // Callback to receive data from DatasetViewer
  const handleDataChange = useCallback(
    (data: SourceDataRecord[], fields: DatasetField[]) => {
      setCurrentData(data);
      setCurrentFields(fields);
    },
    [],
  );

  // Export current table data to CSV
  const handleExportCSV = useCallback(() => {
    if (!currentData.length || !currentFields.length) {
      console.warn("No data available to export");
      return;
    }

    // Get only visible fields
    const visibleFields = currentFields.filter((field) => field.is_visible);

    // Create CSV headers
    const headers = visibleFields.map((field) => field.field_name);

    // Create CSV rows
    const rows = currentData.map((record) => {
      return visibleFields.map((field) => {
        const value = record.data?.[field.field_name] ?? "";
        // Convert all values to strings first to handle any data type
        const stringValue = String(value || "");
        // Handle values that might contain commas, quotes, newlines, carriage returns, or tabs
        if (/[,"\n\r\t]/.test(stringValue)) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      });
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `search_results_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [currentData, currentFields]);

  // Push Smart Search inputs into the URL so DatasetViewer picks them up via query params
  // useEffect(() => {
  //   const searchText = toolResult?.search_query?.search_query
  //     ? toolResult.search_query.search_query
  //     : "";
  //   const predicates = Array.isArray(
  //     toolResult?.search_query?.filter_predicates
  //   )
  //     ? toolResult.search_query.filter_predicates
  //     : [];

  //   // Follow DatasetViewer's pattern: raw JSON for filt, no encodeURIComponent
  //   const nextSearch = `?search_text=${searchText}&filt=${JSON.stringify(
  //     predicates
  //   )}`;
  //   history.replace({ pathname: location.pathname, search: nextSearch });
  // }, []);

  const handleRowSelect = (row: SourceDataRecord) => {
    // Open in new tab with the specific dataset
    const datasetId = Math.abs(row.dataset_fk || toolResult.dataset_id);
    const isDocument = Boolean(row.data?.file_name);

    let url: string;
    if (isDocument) {
      url = `/datasets/${datasetId}/document/${encodeURIComponent(row.id)}`;
    } else {
      url = `/datasets/${datasetId}/predictions/${encodeURIComponent(
        row.source_uid,
      )}`;
    }

    window.open(url, "_blank");
  };
  console.log("CR Search DatasetViewer", toolResult);
  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Dataset Viewer */}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        <DatasetViewer
          mode="embedded"
          overrideDatasetId={String(toolResult.dataset_id)}
          initialSearchQuery={toolResult.search_query.search_query || ""}
          initialFilterPredicates={toolResult.search_query.filter_predicates}
          onRowSelect={handleRowSelect}
          maxSearchResults={maxResults}
          onMaxSearchResultsChange={handleMaxResultsChange}
          onDataChange={handleDataChange}
          hideElements={{
            uploadButton: true,
            downloadButton: true,
          }}
        />
      </Box>

      {/* Results Configuration Bar */}
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: "divider",
          backgroundColor: "background.paper",
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Search Results:
          </Typography>
          <TextField
            type="number"
            size="small"
            value={maxResults}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              if (!isNaN(value)) {
                handleMaxResultsChange(value);
              }
            }}
            inputProps={{
              min: 1,
              max: 1000,
              step: 1,
            }}
            sx={{ width: 100 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            helperText=""
          />
          <Typography variant="body2" color="text.secondary">
            results max (1-1000)
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
            disabled={!currentData.length}
          >
            Export CSV
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default CRSearchDatasetViewer;
